// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#autoGrow();
  }

  handleInput() {
    this.#updateCharacterCount();
    this.#autoGrow();
  }

  /**
   * Solo se enlaza (ver blocks/product-custom-property.liquid) en campos con
   * enable_manual_linebreaks — hoy nada más "Escribe tu frase". Enter solo
   * mantiene el comportamiento de siempre (avanza al siguiente campo del
   * formulario): en un <input> de una sola línea eso pasa nativo, vía
   * envío implícito del form + validación nativa que enfoca el siguiente
   * campo inválido — un <textarea> nunca hace envío implícito, así que hay
   * que replicarlo a mano con requestSubmit(), que dispara exactamente esa
   * misma validación nativa. Shift+Enter no se toca: se deja pasar sin
   * preventDefault para que el navegador inserte el \n como ya hace por
   * default en cualquier textarea.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;

    const { textInput } = this.refs;
    if (!(textInput instanceof HTMLTextAreaElement)) return;

    event.preventDefault();
    textInput.form?.requestSubmit();
  }

  /**
   * Método principal para insertar el salto de línea manual (el botón
   * "+ Salto de línea") — Shift+Enter funciona en desktop pero no es
   * confiable en teclados virtuales de mobile, así que este es el que debe
   * funcionar siempre, en cualquier dispositivo. Inserta \n en la posición
   * real del cursor (no solo al final) vía setRangeText, respeta maxlength
   * igual que si el cliente lo hubiera tecleado, y dispara 'input' a mano
   * porque setRangeText no lo dispara solo.
   */
  insertLineBreak() {
    const { textInput } = this.refs;
    if (!(textInput instanceof HTMLTextAreaElement)) return;

    const maxLength = textInput.maxLength;
    if (maxLength >= 0 && textInput.value.length >= maxLength) return;

    const start = textInput.selectionStart ?? textInput.value.length;
    const end = textInput.selectionEnd ?? textInput.value.length;
    textInput.setRangeText('\n', start, end, 'end');
    textInput.dispatchEvent(new Event('input', { bubbles: true }));
    textInput.focus();
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }

  /**
   * El alto del textarea SIEMPRE lo fija esto, nunca un drag-handle (eso es
   * justo lo que no funciona en mobile, sin mouse para arrastrarlo).
   * Resetear a 'auto' antes de medir scrollHeight es necesario para poder
   * *encoger* de vuelta si el cliente borra una línea, no solo crecer.
   * No-op en un <input> de una sola línea (rows="1" ya le da el alto
   * correcto sin JS).
   */
  #autoGrow() {
    const { textInput } = this.refs;
    if (!(textInput instanceof HTMLTextAreaElement)) return;

    textInput.style.height = 'auto';
    textInput.style.height = `${textInput.scrollHeight}px`;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
